<script setup lang="ts">
import { useForm } from "@inertiajs/vue3"
import { computed, ref, watch } from "vue"
import route from "ziggy-js"
import { ArrowDown, ArrowUp, Pencil, Plus } from "lucide-vue-next"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { useToast } from "@/components/ui/toast/use-toast"

type Category = { id: number; name: string; is_food: boolean }
const props = defineProps<{ open: boolean; categories: Category[] }>()
const emit = defineEmits<{
    "update:open": [value: boolean]
    renamed: [previous: string, current: string]
}>()
const editing = ref<Category | null>(null)
const form = useForm({ name: "", is_food: false })
const { toast } = useToast()
const orderedCategories = ref<Category[]>([])
const orderForm = useForm({ category_ids: [] as number[] })
const busy = computed(() => form.processing || orderForm.processing)
const orderChanged = computed(() => orderedCategories.value.some((category, index) => category.id !== props.categories[index]?.id))

watch(() => props.categories, (categories) => {
    orderedCategories.value = [...categories]
}, { immediate: true })

function moveCategory(index: number, direction: number) {
    const target = index + direction
    if (target < 0 || target >= orderedCategories.value.length) return
    const categories = [...orderedCategories.value]
    ;[categories[index], categories[target]] = [categories[target], categories[index]]
    orderedCategories.value = categories
    orderForm.clearErrors()
}

function saveOrder() {
    orderForm.category_ids = orderedCategories.value.map((category) => category.id)
    orderForm.put(route("recipe-categories.reorder"), {
        preserveScroll: true,
        onSuccess() { toast({ description: "Category order saved." }) },
        onError() { toast({ description: "Could not save category order. Reopen Manage Categories and try again.", variant: "destructive" }) },
    })
}

function reset() {
    editing.value = null
    form.reset()
    form.clearErrors()
}

watch(() => props.open, (open) => {
    if (open) {
        reset()
        orderedCategories.value = [...props.categories]
        orderForm.clearErrors()
    }
})

function edit(category: Category) {
    editing.value = category
    form.name = category.name
    form.is_food = category.is_food
    form.clearErrors()
}

function submit() {
    const previous = editing.value?.name
    const options = {
        preserveScroll: true,
        onSuccess() {
            if (previous) emit("renamed", previous, form.name.trim())
            toast({ description: previous ? "Category updated." : "Category added." })
            reset()
        },
    }
    if (editing.value) {
        form.put(route("recipe-categories.update", editing.value.id), options)
    } else {
        form.post(route("recipe-categories.store"), options)
    }
}
</script>

<template>
    <Dialog :open="open" @update:open="emit('update:open', $event)">
        <DialogContent class="max-h-[90vh] overflow-y-auto sm:max-w-lg">
            <DialogHeader>
                <DialogTitle>Manage Categories</DialogTitle>
                <DialogDescription>Use the arrows and Save Order to arrange categories, or edit their names and recipe type. Changes apply to every recipe in the category.</DialogDescription>
            </DialogHeader>
            <div class="max-h-60 space-y-1 overflow-y-auto rounded-md border p-2">
                <div v-for="(category, index) in orderedCategories" :key="category.id" class="flex items-center justify-between gap-3 rounded px-2 py-1">
                    <div class="min-w-0">
                        <p class="break-words text-sm font-medium">{{ category.name }}</p>
                        <p class="text-xs text-muted-foreground">{{ category.is_food ? "Food" : "Drink" }}</p>
                    </div>
                    <div class="flex shrink-0 items-center gap-1">
                    <Button variant="ghost" size="icon" :disabled="busy || index === 0" :aria-label="`Move ${category.name} up`" @click="moveCategory(index, -1)">
                        <ArrowUp class="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" :disabled="busy || index === orderedCategories.length - 1" :aria-label="`Move ${category.name} down`" @click="moveCategory(index, 1)">
                        <ArrowDown class="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" :disabled="busy || orderChanged" :aria-label="`Edit ${category.name}`" @click="edit(category)">
                        <Pencil class="mr-1 h-3.5 w-3.5" />Edit
                    </Button>
                    </div>
                </div>
            </div>
            <div class="flex items-center justify-between gap-3">
                <p class="text-xs text-muted-foreground">{{ orderChanged ? "Save your order before adding or editing categories." : "Order applies to tabs, recipe lists, and category choices." }}</p>
                <Button type="button" size="sm" :disabled="busy || !orderChanged" @click="saveOrder">{{ orderForm.processing ? "Saving…" : "Save Order" }}</Button>
            </div>
            <form class="space-y-4" @submit.prevent="submit">
                <div class="flex items-center justify-between gap-2">
                    <h3 class="text-sm font-semibold">{{ editing ? "Edit Category" : "New Category" }}</h3>
                    <Button v-if="editing" type="button" variant="ghost" size="sm" :disabled="busy || orderChanged" @click="reset">
                        <Plus class="mr-1 h-3.5 w-3.5" />New Category
                    </Button>
                </div>
                <FormField name="name">
                    <FormItem :error="form.errors.name">
                        <FormLabel>Category name</FormLabel>
                        <FormControl><Input v-model="form.name" placeholder="e.g. Desserts" maxlength="255" required /></FormControl>
                        <FormMessage />
                    </FormItem>
                </FormField>
                <FormField name="is_food">
                    <FormItem :error="form.errors.is_food">
                        <FormLabel>Recipe type</FormLabel>
                        <FormControl>
                            <select v-model="form.is_food" class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                                <option :value="false">Drink</option>
                                <option :value="true">Food</option>
                            </select>
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                </FormField>
                <DialogFooter>
                    <Button type="button" variant="outline" @click="emit('update:open', false)">Close</Button>
                    <Button type="submit" :disabled="busy || orderChanged">{{ form.processing ? "Saving…" : editing ? "Save Changes" : "Add Category" }}</Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>
